import type { CellValueChangedEvent, GridApi, GridReadyEvent } from "ag-grid-community"
import { useCallback, useRef, useState } from "react"

import { COL_HANDLERS } from "../components/tables/cellHandlers"
import { BLANK_PRICING_TIER } from "../components/tables/constants"
import type { PricingGridRow } from "../components/tables/types"

import type { toSurchargeRowUpdate } from "@/screens/Pricing/utils/surcharge"

interface UsePricingTiersActionsReturn {
  rowData: PricingGridRow[]
  modifiedRowsRef: React.MutableRefObject<Set<number>>
  editingSurchargeRowIndex: number | null
  handleGridReady: (event: GridReadyEvent<PricingGridRow>) => void
  handleCellValueChanged: (event: CellValueChangedEvent<PricingGridRow>) => void
  handleAddPriceTier: (rowIndex: number) => void
  handleClearPriceTier: (rowIndex: number) => void
  handleEditSurcharge: (rowIndex: number) => void
  handleSurchargeDialogClose: () => void
  handleSurchargeSubmit: (update: ReturnType<typeof toSurchargeRowUpdate>) => void
  getAllRowData: () => PricingGridRow[]
}

export const usePricingTiersActions = (initialRows: PricingGridRow[]): UsePricingTiersActionsReturn => {
  const [rowData, setRowData] = useState<PricingGridRow[]>(initialRows)
  const modifiedRowsRef = useRef<Set<number>>(new Set())
  const gridApiRef = useRef<GridApi<PricingGridRow>>()
  const [editingSurchargeRowIndex, setEditingSurchargeRowIndex] = useState<number | null>(null)

  const handleGridReady = useCallback((event: GridReadyEvent<PricingGridRow>) => {
    gridApiRef.current = event.api
  }, [])

  const handleCellValueChanged = useCallback((event: CellValueChangedEvent<PricingGridRow>) => {
    const { rowIndex, column, api, node } = event
    if (rowIndex === null) return

    const colId = column.getColId()
    const handler = COL_HANDLERS[colId]

    if (handler) {
      handler({ api, node, rowIndex, modifiedRows: modifiedRowsRef.current })
    }

    if (colId !== "selected") {
      modifiedRowsRef.current.add(rowIndex)
      api.redrawRows({ rowNodes: [node] })
    }
  }, [])

  const handleAddPriceTier = useCallback((rowIndex: number) => {
    if (rowIndex < 0) return
    setRowData((prev) => {
      const origin = prev[rowIndex]
      const updated = [...prev]
      updated.splice(rowIndex + 1, 0, { ...origin, id: "" })
      return updated
    })
  }, [])

  const handleClearPriceTier = useCallback((rowIndex: number) => {
    if (rowIndex < 0) return
    setRowData((prev) => {
      const updated = [...prev]
      updated[rowIndex] = { ...updated[rowIndex], ...BLANK_PRICING_TIER }
      return updated
    })
    modifiedRowsRef.current.delete(rowIndex)
  }, [])

  const handleEditSurcharge = useCallback((rowIndex: number) => {
    if (rowIndex < 0) return
    setEditingSurchargeRowIndex(rowIndex)
  }, [])

  const handleSurchargeDialogClose = useCallback(() => {
    setEditingSurchargeRowIndex(null)
  }, [])

  const handleSurchargeSubmit = useCallback(
    (update: ReturnType<typeof toSurchargeRowUpdate>) => {
      if (editingSurchargeRowIndex === null) return
      const index = editingSurchargeRowIndex
      setRowData((prev) => prev.map((row, i) => (i === index ? { ...row, ...update } : row)))
      modifiedRowsRef.current.add(index)
      setEditingSurchargeRowIndex(null)
    },
    [editingSurchargeRowIndex]
  )

  const getAllRowData = useCallback((): PricingGridRow[] => {
    const rows: PricingGridRow[] = []
    gridApiRef.current?.forEachNode((node) => {
      if (node.data) rows.push(node.data)
    })
    return rows
  }, [])

  return {
    rowData,
    modifiedRowsRef,
    editingSurchargeRowIndex,
    handleGridReady,
    handleCellValueChanged,
    handleAddPriceTier,
    handleClearPriceTier,
    handleEditSurcharge,
    handleSurchargeDialogClose,
    handleSurchargeSubmit,
    getAllRowData,
  }
}
